import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import { PMBarChartCard } from "@pimo/pimo-components/src/lib/pm-components";
import { APP_CRITICALITIES, APP_RECORD_TYPES, ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { CRITICALITY_COLORS_BY_KEY } from "../../../constants";
import {
  DashboardChartEntry,
  getAllReportingItems,
} from "../../../helpers/pages-helpers/dashboard-plugin-helpers";
import { isRecordTypeVisible } from "../../../utils/filter-reporting-items-by-record-type";

const getCriticalityProgressData = (
  items: ReportingItem[]
): { entries: DashboardChartEntry[] } => {
  const criticalityEntries = Object.entries(APP_CRITICALITIES) as [
    keyof typeof APP_CRITICALITIES,
    (typeof APP_CRITICALITIES)[keyof typeof APP_CRITICALITIES],
  ][];

  const countsByCriticality = new Map(
    criticalityEntries.map(([, criticality]) => [criticality, 0])
  );

  for (const item of items) {
    // Items with no criticality set still count — as "n/a" — so the bars add
    // up to the total item count (#2609).
    const criticality =
      item?.basicItem?.criticality ?? APP_CRITICALITIES.NOT_APPLICABLE;
    if (!countsByCriticality.has(criticality)) {
      continue;
    }

    countsByCriticality.set(
      criticality,
      (countsByCriticality.get(criticality) ?? 0) + 1
    );
  }

  const entries = criticalityEntries.map(([key, criticality]) => ({
    label: criticality,
    count: countsByCriticality.get(criticality) ?? 0,
    color: CRITICALITY_COLORS_BY_KEY[key],
  }));

  return { entries };
};

/**
 * The three "criticality" bar charts — DC Business Apps, applications and (when
 * the record type is enabled) IT services (#2222).
 */
export function addCriticalityChartsCard(
  view: View<TestAppState, DragAndDropLayout>
): void {
  const criticalityDCSystemsComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "criticalityDCSystemsDashboardComponent",
      labels: ["barChart", "chart", "criticality", "dcSystems"],
      description: "Shows DC Business Apps split by criticality.",
      title: "Criticality DC Business Apps",
    },
  });

  criticalityDCSystemsComponent.mapState(({ dashboard }) => {
    const allItems = getAllReportingItems(dashboard);
    const { entries } = getCriticalityProgressData(allItems);

    return {
      title: "Criticality DC Business Apps",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Criticality",
      yLabel: "Number of DC Business Apps",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const criticalityApplicationsComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "criticalityApplicationsDashboardComponent",
      labels: ["barChart", "chart", "criticality", "applications"],
      description: "Shows Applications split by criticality.",
      title: "Criticality Applications",
    },
  });

  criticalityApplicationsComponent.mapState(({ dashboard }) => {
    const applicationItems = getAllReportingItems(dashboard).filter(
      (item) =>
        item.basicItem?.recordType === APP_RECORD_TYPES.BUSINESS_APPLICATION
    );
    const { entries } = getCriticalityProgressData(applicationItems);

    return {
      title: "Criticality Applications",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Criticality",
      yLabel: "Number of Applications",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  if (isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)) {
    const criticalityITServicesComponent = view.addComponent({
      component: PMBarChartCard,
      layoutProps: {
        componentId: "criticalityITServicesDashboardComponent",
        labels: ["barChart", "chart", "criticality", "itServices"],
        description: "Shows IT Services split by criticality.",
        title: "Criticality IT Services",
      },
    });

    criticalityITServicesComponent.mapState(({ dashboard }) => {
      const serviceItems = getAllReportingItems(dashboard).filter(
        (item) => item.basicItem?.recordType === APP_RECORD_TYPES.IT_SERVICE
      );
      const { entries } = getCriticalityProgressData(serviceItems);

      return {
        title: "Criticality IT Services",
        categories: entries.map((entry) => entry.label),
        data: entries.map((entry) => entry.count),
        chartType: 1 as const,
        xLabel: "Criticality",
        yLabel: "Number of IT Services",
        colors: entries.map((entry) => entry.color),
        fixedHeight: "320px",
      };
    });
  }
}
